import React from 'react';
import { Order } from '@m4a3/api-client';
import { Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import { getEmojiForCollectorStatus, getDescriptionForCollectorStatus } from '../types/CollectorStatus';
import { orderStatusSequence, PAST_READY_STATUS_ARRAY } from '../../../constants/orderConstants';
import { getStatusData } from '../../../services/order-service';
import { getRequiredActionsForOrder } from '../../../utils/getRequiredActions';
import { isExtraDeliveryOrder, isInHouseMerchant, isDistanceFarToFly } from '../../../utils/orderUtils';
import { inactiveStatuses } from '../../../constants';
import { formatTimeAgo } from '../../../utils/utils';
import { selectMerchants } from '../../../selectors/ordersSelector';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import confirmRouteActionIcon from '../../../assets/svgs/confirmRouteActionIcon.svg';
import assignPadActionIcon from '../../../assets/svgs/assignPadActionIcon.svg';
import suggestDeliverySpotActionIcon from '../../../assets/svgs/suggestDeliverySpotActionIcon.svg';
import { RightPointingArrow } from '../../../components/RightPointingArrow';
import { WaitingImpatientlyIcon } from '../../../components/WaitingImpatientlyIcon';
import { WarningTriangle } from '../../../components/WarningTriangle';
import { FlyingTheDroneCloud } from '../../../components/FlyingTheDroneCloud';
import { DeliveredCloud } from '../../../components/DeliveredCloud';
import { TakingOffCloud } from '../../../components/TakingOffCloud';
import { NewOrderIcon } from '../../../components/NewOrderIcon';
import { OrderActivityRow } from './OrderActivityRow';
import { useTimeTicker } from '../../../contexts/TimeTickerContext';

interface StatusConfig {
  icon: JSX.Element;
  title: string;
  subtitle?: string;
  backgroundColor?: string;
  shouldShow: boolean;
  action?: (e?: React.MouseEvent) => void;
  isRequiredAction?: boolean;
  isTitleRed?: boolean;
}

interface OrderCardStatusIndicatorProps {
  order: Order;
  onOpenRouteConfirmation?: (order: Order) => void;
  openSuggestModal?: () => void;
  onAssignPad?: (e: React.MouseEvent) => void;
  onAcceptOrder?: (order: Order) => void;
  linkedOrdersForOverweightOrders?: {
    linkedOverweightOrderIds?: string[];
    color?: string;
  } | null;
}

export const OrderCardStatusIndicator = ({ 
  order, 
  onOpenRouteConfirmation,
  openSuggestModal,
  onAssignPad,
  onAcceptOrder,
  linkedOrdersForOverweightOrders
}: OrderCardStatusIndicatorProps) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const merchants = useSelector(selectMerchants);

  // Subscribe to global time ticker for efficient reactivity
  const { currentTime } = useTimeTicker();

  // ============ Helper Functions ============

  // Get the appropriate time reference for "Last updated" display
  // ONLY use storePreparedDate when showing the "Prepared" status text
  // For all other statuses (even if order.status is STORE_PREPARED), use modifyDate
  const getTimeReference = (isShowingPreparedStatus: boolean = false): string => {
    if (isShowingPreparedStatus && order.status === 'STORE_PREPARED' && order.storePreparedDate) {
      return order.storePreparedDate;
    }
    return order.modifyDate;
  };

  const getCountdownUntil = (timestampMs: number): string | null => {
    if (!timestampMs) return null;
    const timeDifference = timestampMs - currentTime;
    if (timeDifference <= 0) return null;
    const minutes = Math.floor(timeDifference / 1000 / 60);
    const seconds = Math.floor((timeDifference / 1000) % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // ============ Critical Status Configurations ============

  // Check if order is NEW and needs to be accepted
  const getAcceptNewOrderStatus = (): StatusConfig | null => {
    // Only show for CREATED status (NEW orders)
    if (order.status !== 'CREATED') return null;
    
    return {
      icon: (
        <div style={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          backgroundColor: '#5720B7',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        }}>
          <CheckIcon style={{ color: 'white', fontSize: 24 }} />
        </div>
      ),
      title: 'Accept order',
      backgroundColor: '#875BF7',
      shouldShow: true,
      action: (e?: React.MouseEvent) => {
        console.log('Accept order action triggered - onAcceptOrder:', !!onAcceptOrder);
        if (e) {
          e.stopPropagation();
          e.preventDefault();
        }
        onAcceptOrder?.(order);
      },
      isRequiredAction: true
    };
  };

  // Check if order has been rejected by the store
  const getRejectedOrderStatus = (): StatusConfig | null => {
    if (order.status !== 'REJECTED' && order.status !== 'STORE_REJECTED') return null;
    
    return {
      icon: (
        <div style={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          backgroundColor: '#E61A27',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        }}>
          <CloseIcon style={{ color: 'white', fontSize: 24}} />
        </div>
      ),
      title: 'Store Rejected',
      subtitle: 'Contact customer support.',
      backgroundColor: '#FFF1EB',
      shouldShow: true,
      isTitleRed: true
    };
  };

  // Check if order is in invalid state (RESET + HOLD)
  const getInvalidOrderStateStatus = (): StatusConfig | null => {
    // Only show if order status is RESET and mission status is HOLD
    if (order.status !== 'RESET') return null;
    if ((order as any).missionStatus !== 'HOLD') return null;
    
    return {
      icon: <WarningTriangle size={35} iconColor="red" backgroundColor="#FFF1EB" />,
      title: 'Invalid order state',
      subtitle: "Clear mission airspace", 
      backgroundColor: '#FFF1EB', // Red background
      shouldShow: true,
      isRequiredAction: false
    };
  };

  // Check if order distance is too far (>= 5.5km)
  const getFlyingFarWarningStatus = (): StatusConfig | null => {
    
    const distance = parseFloat((order.missionKmDistance as unknown) as string);
    
    if (!isDistanceFarToFly(distance)) return null;
    
    // Don't show for inactive orders
    if (inactiveStatuses.includes(order.status)) return null;
    
    // Only show if order is at RECEIVED_FROM_STORE or later
    const currentStatusIndex = orderStatusSequence.indexOf(order.status);
    const receivedFromStoreIndex = orderStatusSequence.indexOf('RECEIVED_FROM_STORE');
    if (currentStatusIndex < receivedFromStoreIndex) return null;
    
    return {
      icon: <WarningTriangle size={35} iconColor="#F79009" backgroundColor="#FEF3C7" />,
      title: 'Route distance above 5km',
      subtitle: `Route distance: ${distance}km`,
      backgroundColor: 'white', 
      shouldShow: true,
      isRequiredAction: false
    };
  };

  // Check if order is stuck in ACCEPTED status (not moved to STORE_ACCEPTED within 5 minutes)
  const getWaitingForStoreAcceptStatus = (): StatusConfig | null => {
    // Only show for ACCEPTED status
    if (order.status !== 'ACCEPTED') return null;
    
    // Check how long it's been in ACCEPTED
    const elapsedMs = currentTime - new Date(order.modifyDate).getTime();
    const elapsedMinutes = Math.floor(elapsedMs / 1000 / 60);
    
    // Only show if it's been 5 or more minutes
    if (elapsedMinutes < 5) return null;
    
    return {
      icon: <WaitingImpatientlyIcon size={25} iconColor="white" backgroundColor="#F79009" />,
      title: 'Waiting for store to accept',
      subtitle: `Last updated: ${formatTimeAgo(getTimeReference(false))}`,
      backgroundColor: '#FEF3C7', // Yellow background (will turn red via warning system)
      shouldShow: true,
      isRequiredAction: false
    };
  };

  // Check if order is overweight (≥2kg) and has no linked orders
  const getActualOverweightOrderStatus = (): StatusConfig | null => {

    let orderWeight: number = 0;

    if (order?.actualWeight) {
      orderWeight = order?.actualWeight;
    } else {
      return null;
    }

    if (orderWeight == null) return null; // No weight data available
    
    const isOverweight = orderWeight > 2;
    const hasNoLinkedOrders = !linkedOrdersForOverweightOrders?.linkedOverweightOrderIds?.length;
    
    if (!isOverweight || !hasNoLinkedOrders) return null;
    
    return {
      icon: <WaitingImpatientlyIcon size={25} iconColor="white" backgroundColor="#F79009" />,
      title: 'Overweight order',
      subtitle: `Last updated: ${formatTimeAgo(getTimeReference(false))}`, // Always use modifyDate
      backgroundColor: '#FEF3C7', // Yellow background
      shouldShow: true,
      isRequiredAction: false
    };
  };


  //Actual weight (from weighing the package) or total weight (ie estimated weight - from item weights listed on merchant menu) in kg
  const getOrderWeightInKg = (): number | null => {
    if (order?.actualWeight) {
      return order?.actualWeight;
    }

    if ((order as any)?.totalWeight) {
        return (order as any)?.totalWeight / 1000;
    }

    return null;
  };

  const getEstimatedOverweightOrderStatus = (): StatusConfig | null => {

    if (inactiveStatuses.includes(order.status)) return null;

    // Check if order is overweight (≥2kg) and has no linked orders
    let orderWeight: number = 0;

    if ((order as any)?.totalWeight) {
      orderWeight = (order as any)?.totalWeight / 1000;
    } else {
      return null;
    }

    if (orderWeight == null) return null; // No weight data available
    
    const isOverweight = orderWeight > 2;
    const hasNoLinkedOrders = !linkedOrdersForOverweightOrders?.linkedOverweightOrderIds?.length;
    
    if (!isOverweight || !hasNoLinkedOrders) return null;
    
    return {
      icon: <WaitingImpatientlyIcon size={25} iconColor="white" backgroundColor="#F79009" />,
      title: 'Estimated weight exceeds 2kg',
      subtitle: `Last updated: ${formatTimeAgo(getTimeReference(false))}`, // Always use modifyDate
      backgroundColor: '#FEF3C7', // Yellow background
      shouldShow: true,
      isRequiredAction: false
    };
  };

  // ============ Required Action Status Configurations ============

  const getRequiredActionStatus = (): StatusConfig | null => {
    const requiredActions = getRequiredActionsForOrder(order);
    if (requiredActions.length === 0) return null;
    
    // Show first required action (highest priority)
    const firstAction = requiredActions[0];
    
    const iconMap = {
      'confirm-route': <img src={confirmRouteActionIcon} alt="Confirm route" style={{ width: 40, height: 40 }} />,
      'assign-pad': <img src={assignPadActionIcon} alt="Assign pad" style={{ width: 40, height: 40 }} />,
      'suggest-delivery-spot': <img src={suggestDeliverySpotActionIcon} alt="Suggest delivery spot" style={{ width: 40, height: 40 }} />
    };
    
    const actionMap = {
      'confirm-route': (e?: React.MouseEvent) => {
        console.log('Confirm route action - onOpenRouteConfirmation:', !!onOpenRouteConfirmation);
        onOpenRouteConfirmation?.(order);
      },
      'assign-pad': (e?: React.MouseEvent) => {
        console.log('Assign pad action - onAssignPad:', !!onAssignPad, 'event:', e);
        if (e) {
          onAssignPad?.(e);
        }
      },
      'suggest-delivery-spot': (e?: React.MouseEvent) => {
        console.log('Suggest delivery spot action - openSuggestModal:', !!openSuggestModal);
        openSuggestModal?.();
      }
    };

    return {
      icon: iconMap[firstAction.id],
      title: firstAction.title,
      backgroundColor: '#875BF7',
      shouldShow: true,
      action: actionMap[firstAction.id],
      isRequiredAction: true
    };
  };

  // ============ Status Configurations ============

  const getTakeoffStatus = (): StatusConfig | null => {
    const countdown = getCountdownUntil(order.takeOffTimeMs);
    if (!countdown) return null;
    if (!order.originLandingPadId) return null;

    return {
      icon: <TakingOffCloud size={25} iconColor="#14142B" backgroundColor="#F2F4F7" />,
      title: 'Taking off in',
      subtitle: countdown,
      shouldShow: true
    };
  };

  const getCollectorStatus = (
    requiredStatus: string,
    options?: { 
      requireStorePrep?: boolean
    }
  ): StatusConfig | null => {
    // Don't show collector statuses for:
    // 1. Orders past RECEIVED_FROM_STORE (READY, DISPATCHED, DELIVERED, etc.)
    // 2. Inactive orders (COMPLETE, CANCELLED, etc.)
    // 3. Extra delivery orders
    // 4. In-house merchant orders
    if (PAST_READY_STATUS_ARRAY.includes(order.status) || 
        inactiveStatuses.includes(order.status) || 
        isExtraDeliveryOrder(order) || 
        isInHouseMerchant((order as any).posId, merchants)) return null;
    
    if (order.collectorStatus !== requiredStatus) return null;
    if (requiredStatus !== 'WAITING_FOR_COLLECTOR_TO_ACCEPT' && !order.collectorId) return null;
    
    // Check if order must be at STORE_PREPARED or later
    if (options?.requireStorePrep) {
      const currentStatusIndex = orderStatusSequence.indexOf(order.status);
      const storePreparedIndex = orderStatusSequence.indexOf('STORE_PREPARED');
      if (currentStatusIndex < storePreparedIndex) return null;
    }
    
    const emoji = getEmojiForCollectorStatus(order.collectorStatus);
    const description = getDescriptionForCollectorStatus(order.collectorStatus, order.collectorId);
    
    return {
      icon: <span style={{ fontSize: 20 }}>{emoji}</span>,
      title: description,
      subtitle: `Last updated: ${formatTimeAgo(getTimeReference(false))}`,
      shouldShow: true
    };
  };

  const getFlyingToLocationStatus = (): StatusConfig | null => {
    if (order.status !== 'DISPATCHED') return null;
    
    const locationId = order.eircode || 'location';
    
    return {
      icon: <FlyingTheDroneCloud size={25} iconColor="#14142B" backgroundColor="#F2F4F7" />,
      title: `Flying to ${locationId}`,
      subtitle: `Last updated: ${formatTimeAgo(getTimeReference(false))}`,
      shouldShow: true
    };
  };

  const getFlyingBackToBaseStatus = (): StatusConfig | null => {
    if (order.status !== 'DELIVERED') return null;
    
    return {
      icon: <DeliveredCloud size={25} />,
      title: 'Delivered. Flying back.',
      subtitle: `Last updated: ${formatTimeAgo(getTimeReference(false))}`,
      shouldShow: true
    };
  };

  const getNewOrderStatus = (): StatusConfig | null => {
    // Only show for CREATED status
    if (order.status !== 'CREATED') return null;
    
    const statusData = getStatusData(order.status);
    
    return {
      icon: <NewOrderIcon size={25} iconColor="#14142B" backgroundColor="#F2F4F7" />,
      title: statusData.text,
      subtitle: `Last updated: ${formatTimeAgo(getTimeReference(false))}`,
      shouldShow: true
    };
  };

  const getLastUpdatedStatus = (): StatusConfig | null => {
    // Fallback status - always shown if no other status matches
    const statusData = getStatusData(order.status);
    const isInactive = inactiveStatuses.includes(order.status);
    // Only use storePreparedDate when showing "Prepared" status
    const isShowingPreparedStatus = order.status === 'STORE_PREPARED';
    const subtitle = isShowingPreparedStatus 
      ? `${formatTimeAgo(getTimeReference(true))}` 
      : `Last updated: ${formatTimeAgo(getTimeReference(false))}`;
    
    return {
      icon: <RightPointingArrow size={25} arrowColor="#6E7191" backgroundColor="#F2F4F7" strokeWidth={2} />,
      title: statusData.text,
      subtitle: isInactive ? undefined : subtitle, // No subtitle for inactive orders
      shouldShow: true
    };
  };

  // ============ Priority System ============
  // First matching status wins 
  const getActiveStatus = (): StatusConfig | null => {
    const statusChecks = [
      
      // Required actions - uses shared utility
      getRequiredActionStatus,
      // Accept NEW order - highest priority
      getAcceptNewOrderStatus,
      // Store rejected order
      getRejectedOrderStatus,
      // Invalid order state (RESET + HOLD)
      getInvalidOrderStateStatus,
      // Waiting for store to accept (ACCEPTED status stuck for 5+ minutes)
      getWaitingForStoreAcceptStatus,
      // Overweight order
      getActualOverweightOrderStatus,
      // Normal statuses
      getTakeoffStatus,
      getFlyingToLocationStatus,
      getFlyingBackToBaseStatus,

      // Distance to drop nearing permitted threshold - should only show after a route is confirmed
      getFlyingFarWarningStatus,
      () => getCollectorStatus('HANDED_OVER'),
      () => getCollectorStatus('AT_MANNA_BASE'),
      () => getCollectorStatus('COLLECTOR_NEAR_LZ'),
      () => getCollectorStatus('EN_ROUTE_TO_MANNA_BASE'),
      () => getCollectorStatus('WAITING_AT_MERCHANT'),
      () => getCollectorStatus('COLLECTOR_NEAR_MERCHANT'),
      () => getCollectorStatus('EN_ROUTE_TO_MERCHANT'),

      // Estimated overweight order 
      getEstimatedOverweightOrderStatus, 

      () => getCollectorStatus('COLLECTOR_ASSIGNED'),
      () => getCollectorStatus('WAITING_FOR_COLLECTOR_TO_ACCEPT', { 
        requireStorePrep: true
      }),
      // New order status (before fallback)
      getNewOrderStatus,
      // Fallback - always shows if nothing else matches
      getLastUpdatedStatus,
    ];

    for (const checkStatus of statusChecks) {
      const status = checkStatus();
      if (status?.shouldShow) {
        return status;
      }
    }

    return null;
  };

  const activeStatus = getActiveStatus();

  // Don't render anything if no active status
  if (!activeStatus) return null;

  // ============ Warning System ============
  // Constants
  const DEFAULT_YELLOW_WARNING_MINUTES = 10;
  const DEFAULT_RED_WARNING_MINUTES = 15;
  const CRITICAL_WARNING_OFFSET_MINUTES = 5;
  
  // Helper function to determine warning thresholds based on order state
  const getWarningThresholds = (): { yellow: number; red: number } => {
    
    // OVERWEIGHT ORDER: Immediate yellow, red after 5 mins
    const orderWeight = getOrderWeightInKg();
    const isOverweight = orderWeight != null && orderWeight > 2;
    const hasNoLinkedOrders = !linkedOrdersForOverweightOrders?.linkedOverweightOrderIds?.length;
    if (isOverweight && hasNoLinkedOrders) {
      return {
        yellow: 0, // Immediately yellow
        red: CRITICAL_WARNING_OFFSET_MINUTES // Red after 5 mins
      };
    }
    
    // ACCEPTED STATUS: Yellow at 5 minutes, red at 10 minutes
    if (order.status === 'ACCEPTED') {
      return {
        yellow: 5, // Yellow when 5+ minutes in ACCEPTED
        red: 8 // Red when 8+ minutes in ACCEPTED
      };
    }

    // STORE_ACCEPTED / STORE_PREPARING STATUS: Yellow at 25 minutes, red at 35 minutes
    if (order.status === 'STORE_ACCEPTED' || order.status === 'STORE_PREPARING') {
      return {
        yellow: DEFAULT_YELLOW_WARNING_MINUTES,
        red: DEFAULT_RED_WARNING_MINUTES
      };
    }
    
    // EN_ROUTE_TO_MERCHANT: Use merchantTravelTime if available
    if (order.collectorStatus === 'EN_ROUTE_TO_MERCHANT') {
      const merchantTravelTime = (order as any).merchantTravelTime;
      if (merchantTravelTime != null) {
        return {
          yellow: merchantTravelTime,
          red: merchantTravelTime + CRITICAL_WARNING_OFFSET_MINUTES
        };
      }
    }
    
    // Add more special cases here as needed
    // Example:
    // if (order.status === 'SOME_OTHER_STATUS') {
    //   return { yellow: X, red: Y };
    // }
    
    // Default for all other statuses
    return {
      yellow: DEFAULT_YELLOW_WARNING_MINUTES,
      red: DEFAULT_RED_WARNING_MINUTES
    };
  };
  
  // Calculate elapsed time
  // For warnings, always use modifyDate unless we're showing the "Prepared" status
  const isShowingPreparedStatusForWarnings = activeStatus.title === getStatusData('STORE_PREPARED')?.text;
  const elapsedMs = currentTime - new Date(getTimeReference(isShowingPreparedStatusForWarnings)).getTime();
  const elapsedMinutes = Math.floor(elapsedMs / 1000 / 60);
  const isInactive = inactiveStatuses.includes(order.status);
  
  // Warnings don't apply to required actions (they stay purple) or inactive orders
  const canShowWarnings = !activeStatus.isRequiredAction && !isInactive;
  
  // Get thresholds and determine warnings
  const thresholds = getWarningThresholds();
  const shouldShowWarningBackground = canShowWarnings && elapsedMinutes > thresholds.yellow;
  const shouldShowCriticalWarning = canShowWarnings && elapsedMinutes > thresholds.red;
  
  // Determine background color: Critical (threshold+5 min) > Warning (threshold min) > Normal
  let finalBackgroundColor = activeStatus.backgroundColor;
  if (shouldShowCriticalWarning) {
    finalBackgroundColor = '#FFF1EB'; // rgba(252, 80, 0, 0.08) on white background
  } else if (shouldShowWarningBackground) {
    finalBackgroundColor = '#FEF3C7'; // Yellow warning
  }
  
  // Use appropriate warning icon based on severity (but not for CREATED orders or inactive orders)
  let finalIcon = activeStatus.icon;
  if (shouldShowWarningBackground && order.status !== 'CREATED' && !isInactive) {
    if (shouldShowCriticalWarning) {
      // Red background: use red WarningTriangle
      finalIcon = <WarningTriangle size={35} iconColor="#E61A27" backgroundColor="#FFF1EB" />;
    } else {
      // Yellow background: use orange OrderCardWarningIcon
      finalIcon = <WaitingImpatientlyIcon size={25} iconColor="white" backgroundColor="#F79009" />;
    }
  }

  // Helper function to get warning title override (if needed)
  const getWarningTitleOverride = (): string | null => {
    if (!shouldShowWarningBackground) return null;
    
    // EN_ROUTE_TO_MERCHANT: Override title when in warning state
    if (order.collectorStatus === 'EN_ROUTE_TO_MERCHANT') {
      return 'Waiting for collector to arrive';
    }
    
    // Add more title overrides here as needed
    // Example:
    // if (order.status === 'SOME_OTHER_STATUS') {
    //   return 'Custom warning title';
    // }
    
    return null;
  };

  // Determine final title
  const finalTitle = getWarningTitleOverride() ?? activeStatus.title;

  const handleClick = (e: React.MouseEvent) => {
    if (activeStatus.action) {
      e.stopPropagation();
      e.preventDefault();
      console.log('OrderCardStatusIndicator action triggered:', activeStatus.title);
      // Pass the actual event to the action so popups can anchor to it
      activeStatus.action(e);
    }
  };

  return (
    <OrderActivityRow
      ref={containerRef}
      icon={finalIcon}
      title={finalTitle}
      subtitle={activeStatus.subtitle}
      backgroundColor={finalBackgroundColor}
      isRequiredAction={activeStatus.isRequiredAction}
      showChevron={!!activeStatus.isRequiredAction}
      subtitleColor={
        activeStatus.isRequiredAction
          ? 'rgba(255,255,255,0.8)'
          : shouldShowCriticalWarning
          ? '#FC5000'
          : shouldShowWarningBackground
          ? '#DC6803'
          : '#6E7191'
      }
      hasAction={!!activeStatus.action}
      onClick={handleClick}
    />
  );
};

